import { useLiveQuery } from "dexie-react-hooks";
import { useEffect, useMemo, useState } from "react";
import { BrandMark } from "./BrandMark";
import { ThemeToggle } from "./ThemeToggle";
import { BriefcaseBusiness, ListTree } from "lucide-react";
import { cacheFiles, cacheGuide, db, type QueueAction } from "../lib/db";
import { loadReviewBundle, type FixtureDescriptor } from "../lib/fixtures";
import { preloadHostedPull } from "../lib/hosted";
import { handleLocalNavigation, navigate } from "../lib/navigation";
import { usePageMetadata } from "../hooks/usePageMetadata";
import { nextStackMember, stackPrerequisitesSatisfied } from "../lib/review-stack";
import { completedFixtureForMarker, completedFixtureIdsForCurrentHeads } from "../lib/queue-completion";
import { compareQueue, type QueueLens } from "../lib/queue-order";
import { skippedFixtureIdsForCurrentHeads } from "../lib/queue-skips";
import type { QueueRoutingDiagnostics } from "../../shared/queue-routing";
import {
  Popover,
  PopoverContent,
  PopoverDescription,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
} from "./ui/popover";
import { repositoryShortName } from "../lib/pull-view";

type SkipReason = QueueAction["reason"];

const skipReasons: Array<{ id: SkipReason; label: string }> = [
  { id: "not-my-area", label: "Not my area" },
  { id: "too-large", label: "Too large right now" },
  { id: "needs-specialist", label: "Needs a specialist" },
  { id: "not-ready", label: "Not ready" },
  { id: "guide-unclear", label: "Guide is unclear" },
];

function difficultyLabel(fixture: FixtureDescriptor) {
  if (fixture.queue.difficulty === "tough") return "Tough Chew";
  if (fixture.queue.difficulty === "quick") return "Quick chew";
  return "Normal review";
}

const breadthLabels = {
  tweak: "Tweak",
  focused: "Focused change",
  "cross-cutting": "Cross-cutting",
  system: "System change",
} as const;

function reviewPath(fixture: FixtureDescriptor): string {
  return fixture.reviewPath ?? `/review/${fixture.id}`;
}

const chewfileLabels = {
  guided: "Guided",
  description: "PR guide",
  unguided: "Unguided",
  "required-missing": "Chewfile required",
  stale: "Chewfile stale",
} as const;

const assignmentLabels = {
  "requested-reviewer": "Review requested",
  "requested-team": "Team review requested",
  assignee: "Assigned",
  "general-pool": "General pool",
  author: "Author",
  "other-reviewers-requested": "Routed elsewhere",
} as const;

export function LandingPage({
  fixtures,
  hosted = false,
  routing,
}: {
  fixtures: FixtureDescriptor[];
  hosted?: boolean;
  routing?: QueueRoutingDiagnostics;
}) {
  usePageMetadata("CHEW · Review queue", "The best next pull request for this reviewer.");
  const [lens, setLens] = useState<QueueLens>(() =>
    localStorage.getItem("chew:queue-lens") === "quick-wins" ? "quick-wins" : "anything-useful",
  );
  const [skipOpen, setSkipOpen] = useState(false);
  const [queueOpen, setQueueOpen] = useState(false);
  const sessions = useLiveQuery(() => db.sessions.orderBy("lastOpenedAt").toArray()) ?? [];
  const queueActions = useLiveQuery(() => db.queueActions.where("type").equals("skip").toArray());
  const completionMarker = new URLSearchParams(window.location.search);
  const justChewed = completionMarker.get("chewed");
  const justChewedHead = completionMarker.get("chewedHead");
  const completedIds = useMemo(
    () => completedFixtureIdsForCurrentHeads(fixtures, sessions),
    [fixtures, sessions],
  );
  const skipped = useMemo(
    () => skippedFixtureIdsForCurrentHeads(fixtures, queueActions ?? []),
    [fixtures, queueActions],
  );
  const justChewedFixture = completedFixtureForMarker(fixtures, justChewed, justChewedHead);
  const stackContinuation = nextStackMember(justChewedFixture, fixtures, completedIds);

  const ordered = useMemo(() => {
    const candidates = fixtures.filter((fixture) =>
      !fixture.draft
      && !completedIds.has(fixture.id)
      && !skipped.has(fixture.id)
      && fixture.claim?.status !== "held"
      && stackPrerequisitesSatisfied(fixture, fixtures, completedIds),
    );
    const ranked = candidates.sort((left, right) => compareQueue(lens, left, right));
    if (!stackContinuation || stackContinuation.claim?.status === "held") return ranked;
    return [stackContinuation, ...ranked.filter((candidate) => candidate.id !== stackContinuation.id)];
  }, [completedIds, fixtures, lens, skipped, stackContinuation]);
  const inspectedQueue = useMemo(
    () => fixtures.filter((fixture) => !fixture.draft).sort((left, right) => compareQueue(lens, left, right)),
    [fixtures, lens],
  );
  const next = ordered[0];
  const completedCount = completedIds.size;

  useEffect(() => {
    if (!next) return;
    let cancelled = false;
    if (hosted && next.mode === "live" && next.reviewable !== false) {
      const warm = () => {
        if (!cancelled) void preloadHostedPull(next.repository, next.pullNumber).catch(() => undefined);
      };
      const timer = window.setTimeout(warm, 120);
      return () => {
        cancelled = true;
        window.clearTimeout(timer);
      };
    }
    if (next.mode === "live") return;
    void loadReviewBundle(next)
      .then(async ({ config, files, guide }) => {
        if (cancelled) return;
        await cacheGuide(next, guide, config);
        await cacheFiles(next.id, files);
      })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, [hosted, next]);

  const changeLens = (nextLens: QueueLens) => {
    setLens(nextLens);
    localStorage.setItem("chew:queue-lens", nextLens);
    setSkipOpen(false);
  };

  const skip = async (reason: SkipReason) => {
    if (!next) return;
    await db.queueActions.add({
      id: crypto.randomUUID(),
      fixtureId: next.id,
      headSha: next.headSha,
      type: "skip",
      reason,
      createdAt: new Date().toISOString(),
    });
    setSkipOpen(false);
  };

  const restoreSkipped = async () => {
    await db.queueActions.where("type").equals("skip").delete();
  };

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const isTyping = Boolean(target?.closest("input, textarea, select, [contenteditable='true']"));
      if (isTyping || event.altKey || event.ctrlKey || event.metaKey) return;
      if (event.key === "Escape" && skipOpen) {
        event.preventDefault();
        setSkipOpen(false);
        return;
      }
      if (event.key === "Escape" && queueOpen) {
        event.preventDefault();
        setQueueOpen(false);
        return;
      }
      if (skipOpen && /^[1-5]$/.test(event.key)) {
        event.preventDefault();
        const reason = skipReasons[Number(event.key) - 1];
        if (reason) void skip(reason.id);
        return;
      }
      if (event.key === "1") changeLens("anything-useful");
      if (event.key === "2") changeLens("quick-wins");
      if (event.key.toLowerCase() === "s") {
        event.preventDefault();
        setSkipOpen((open) => !open);
      }
      if (event.key.toLowerCase() === "q") {
        event.preventDefault();
        setQueueOpen((open) => !open);
      }
      if (hosted && event.key.toLowerCase() === "m") {
        event.preventDefault();
        navigate("/");
      }
      if (next && next.reviewable !== false && (event.key === "ArrowRight" || (event.key === "Enter" && !target?.closest("button, a")))) {
        event.preventDefault();
        navigate(reviewPath(next));
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [hosted, next, queueOpen, skipOpen]);

  return (
    <main className="queue-shell">
      <header className="queue-header">
        <a className="brand" href="/" onClick={(event) => handleLocalNavigation(event, "/")}><BrandMark /><strong>CHEW</strong></a>
        <div className="queue-lenses" aria-label="Queue lens">
          <button className={lens === "anything-useful" ? "active" : ""} onClick={() => changeLens("anything-useful")} type="button">Anything useful</button>
          <button className={lens === "quick-wins" ? "active" : ""} onClick={() => changeLens("quick-wins")} type="button">Quick wins</button>
        </div>
        <div className="queue-header-tools">
          <span className="chewed-count">{completedCount > 0 ? `${completedCount} ${completedCount === 1 ? "PR" : "PRs"} chewed` : hosted ? "Live pilot" : "Local prototype"}</span>
          {hosted && (
            <a className="queue-inspector-trigger" href="/" onClick={(event) => handleLocalNavigation(event, "/")}>
              <BriefcaseBusiness aria-hidden="true" /><span>My work</span><kbd>M</kbd>
            </a>
          )}
          {hosted && (
            <a className="queue-inspector-trigger" href="/analytics" onClick={(event) => handleLocalNavigation(event, "/analytics")}>
              <span>Analytics</span>
            </a>
          )}
          <Popover
            onOpenChange={setQueueOpen}
            open={queueOpen}
          >
            <PopoverTrigger
              render={<button aria-label={`Inspect queue, ${fixtures.length} PR${fixtures.length === 1 ? "" : "s"}`} className="queue-inspector-trigger" type="button" />}
            >
              <ListTree aria-hidden="true" />
              <span>Queue</span>
              <small>{fixtures.length}</small>
              <kbd>Q</kbd>
            </PopoverTrigger>
            <PopoverContent align="end" className="queue-inspector" side="bottom" sideOffset={7}>
              <PopoverHeader className="queue-inspector-header">
                <PopoverTitle>Current queue</PopoverTitle>
                <PopoverDescription>Read-only · ranked by {lens === "quick-wins" ? "Quick wins" : "Anything useful"}</PopoverDescription>
              </PopoverHeader>
              <ol className="queue-inspector-list">
                {inspectedQueue.map((fixture, index) => {
                  const completed = completedIds.has(fixture.id);
                  const wasSkipped = skipped.has(fixture.id);
                  const waitingForStack = !stackPrerequisitesSatisfied(fixture, fixtures, completedIds);
                  const blocked = fixture.reviewable === false || waitingForStack;
                  const position = completed ? "Completed" : wasSkipped ? "Skipped" : waitingForStack ? "Waiting"
                    : blocked ? "Blocked"
                    : fixture.claim?.status === "owned" ? "Yours"
                      : fixture.claim?.status === "held" ? "Claimed"
                        : fixture.id === next?.id ? "Next" : "Queued";
                  return (
                    <li className={`queue-inspector-item${fixture.id === next?.id ? " is-next" : ""}${blocked ? " is-blocked" : ""}`} key={fixture.id}>
                      <span className="queue-inspector-rank">{String(index + 1).padStart(2, "0")}</span>
                      <div className="queue-inspector-copy">
                        <strong><span>#{fixture.pullNumber}</span>{fixture.title}</strong>
                        <small>
                          {fixture.chewfile ? chewfileLabels[fixture.chewfile.status] : "Local fixture"}
                          <i>·</i>{fixture.queue.estimatedMinutes} min
                          <i>·</i>{fixture.stats.changedFiles} files
                          {fixture.assignment && <><i>·</i>{assignmentLabels[fixture.assignment.reason]}</>}
                          {fixture.claim?.status === "held" && <><i>·</i>@{fixture.claim.reviewerLogin}</>}
                        </small>
                        {waitingForStack ? <em>Waiting for {(fixture.scope.stack.reviewAfter ?? []).map((pull) => `#${pull}`).join(", ")}</em>
                          : blocked && fixture.chewfile?.requiredReasons.length ? <em>{fixture.chewfile.requiredReasons.join(" · ")}</em> : null}
                      </div>
                      <span className="queue-inspector-state">{position}</span>
                    </li>
                  );
                })}
                {inspectedQueue.length === 0 && (
                  <li className="queue-inspector-empty">
                    {routing
                      ? `CHEW evaluated ${routing.summary.openPulls} open ${routing.summary.openPulls === 1 ? "PR" : "PRs"}; none is currently in your queue.`
                      : "No PRs were returned for this reviewer."}
                  </li>
                )}
              </ol>
            </PopoverContent>
          </Popover>
          <ThemeToggle />
        </div>
      </header>

      {justChewedFixture && (
        <div className="queue-reward" role="status">
          <span>✓</span>
          <strong>{justChewedFixture.queue.difficulty === "tough" ? "That one had some bite" : "Nicely chewed"}</strong>
        </div>
      )}

      <section className="next-review">
        {next ? (
          <>
            <div className="next-kicker">
              {next.id === stackContinuation?.id ? "Continue the stack" : next.claim?.status === "owned" ? "Continue review" : next.claim?.status === "held" ? "Already being reviewed" : "Best next review"}
            </div>
            <article className="next-card">
              <header>
                <div>
                  <span>{repositoryShortName(next.repository)}</span>
                  <strong>#{next.pullNumber}</strong>
                </div>
                <div className="queue-scope-badges">
                  {next.draft && <span>Draft</span>}
                  {next.chewfile && <span className={`chewfile-state chewfile-${next.chewfile.status}`}>{chewfileLabels[next.chewfile.status]}</span>}
                  <span>{breadthLabels[next.scope.breadth]}</span>
                  <span className={`difficulty difficulty-${next.queue.difficulty}`}>{difficultyLabel(next)}</span>
                </div>
              </header>
              {next.id === stackContinuation?.id && (
                <div className="queue-stack-continuation">
                  <span>Part {next.scope.stack.position} of {next.scope.stack.total}</span>
                  <strong>{next.scope.stack.title ?? "Continue while the context is warm"}</strong>
                  <small>Shared stack context is already loaded.</small>
                </div>
              )}
              <h1>{next.title}</h1>
              <p className="queue-reason">{next.queue.reason}</p>
              {next.claim?.status === "held" && (
                <p className="queue-claim-note">@{next.claim.reviewerLogin} is active in {next.claim.slotLabel}. This is only a heads-up; you can still review it.</p>
              )}
              {next.reviewable === false && next.chewfile && (
                <p className="queue-blocked-reason">{next.chewfile.requiredReasons.join(" · ")}</p>
              )}
              <div className="queue-meta">
                <span>About {next.queue.estimatedMinutes} min</span>
                <span>{next.stats.changedFiles} files</span>
                <span>{next.queue.reviewGap}</span>
              </div>
              <div className="queue-areas">
                {next.queue.areas.map((area) => <span key={area}>{area}</span>)}
              </div>
              <footer>
                <div className="skip-control">
                  <button className="skip-button" onClick={() => setSkipOpen((open) => !open)} type="button">Skip</button>
                  {skipOpen && (
                    <div className="skip-menu">
                      <span>Why skip this one?</span>
                      {skipReasons.map((reason) => (
                        <button key={reason.id} onClick={() => void skip(reason.id)} type="button">{skipReasons.indexOf(reason) + 1} · {reason.label}</button>
                      ))}
                    </div>
                  )}
                </div>
                {next.reviewable === false ? (
                  <a className="secondary-button" href={next.url} rel="noreferrer" target="_blank">Open on GitHub</a>
                ) : (
                  <a
                    className="primary-button"
                    href={reviewPath(next)}
                    onClick={(event) => handleLocalNavigation(event, reviewPath(next))}
                    onPointerEnter={() => {
                      if (hosted && next.mode === "live") void preloadHostedPull(next.repository, next.pullNumber).catch(() => undefined);
                    }}
                  >
                    {next.claim?.status === "held" ? "Review anyway" : next.claim?.status === "owned" ? "Continue review" : next.chewfile?.status === "unguided" ? "Review unguided" : next.scope.breadth === "tweak" ? "Quick review" : "Review"}
                    <kbd aria-hidden="true">↵</kbd><span aria-hidden="true">→</span>
                  </a>
                )}
              </footer>
            </article>
          </>
        ) : (
          <div className="queue-empty">
            <span>✓</span>
            <h1>Nothing useful right now.</h1>
            <p>{hosted && routing
              ? `CHEW sees ${routing.summary.openPulls} open ${routing.summary.openPulls === 1 ? "PR" : "PRs"}, but none currently routes to you.`
              : hosted ? "No open PR currently routes to you." : "You have reached the end of this local queue."}</p>
            {hosted && routing && <a href="/" onClick={(event) => handleLocalNavigation(event, "/")}>See every routing decision</a>}
            {skipped.size > 0 && <button onClick={() => void restoreSkipped()} type="button">Bring skipped PRs back</button>}
          </div>
        )}
      </section>
    </main>
  );
}
